import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { useTheme } from "../context/ThemeContext";

export default function Settings({ onClose }) {
    const uid = auth.currentUser?.uid;
    const [email, setEmail] = useState(auth.currentUser?.email || "");
    const [name, setName] = useState("");
    const [username, setUsername] = useState("");
    const [mobile, setMobile] = useState("");
    const [photoURL, setPhotoURL] = useState(auth.currentUser?.photoURL || "");
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState("");
    const [showLastSeen, setShowLastSeen] = useState(true); // 👈 New State

    const { isDarkMode, toggleTheme } = useTheme();

    useEffect(() => {
        const load = async () => {
            const snap = await getDoc(doc(db, "users", uid));
            if (snap.exists()) {
                const d = snap.data();
                setName(d.name || "");
                setUsername(d.username || "");
                setMobile(d.mobile || "");
                if (d.photoURL) setPhotoURL(d.photoURL);
                if (d.showLastSeen !== undefined) setShowLastSeen(d.showLastSeen);
            }
            setLoading(false);
        };
        load();
    }, [uid]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await updateDoc(doc(db, "users", uid), {
                name,
                username,
                mobile,
                photoURL, // Ensure photo is synced if changed elsewhere
                showLastSeen, // 👈 Save Preference
            });
            setStatus("✅ Settings updated");
            setTimeout(() => setStatus(""), 3000);
        } catch (e) {
            console.error(e);
            setStatus("❌ Update failed");
        }
    };



    if (loading) return <div className="fixed inset-0 bg-black/50 flex items-center justify-center text-white z-50">Loading settings...</div>;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 animate-fade-in">
            <div className={`w-[95%] max-w-lg rounded-3xl shadow-2xl relative overflow-hidden flex flex-col max-h-[90vh] transition-colors duration-300 ${isDarkMode ? 'bg-[#1e293b] border border-white/10 text-white' : 'bg-white text-gray-900'}`}>

                {/* Header */}
                <div className={`px-6 py-5 flex items-center justify-between border-b ${isDarkMode ? 'border-white/10 bg-white/5' : 'border-gray-100 bg-gray-50'}`}>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white shadow-lg">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
                        </div>
                        <h2 className="text-xl font-bold tracking-tight">Settings</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className={`p-2 rounded-full transition-colors ${isDarkMode ? 'hover:bg-white/10 text-gray-400 hover:text-white' : 'hover:bg-gray-200 text-gray-500 hover:text-black'}`}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </button>
                </div>

                <div className="overflow-y-auto custom-scrollbar p-6 space-y-8">

                    <section>
                        <h3 className={`text-xs font-bold uppercase tracking-wider mb-4 px-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Privacy</h3>
                        <div className={`p-4 rounded-2xl border flex items-center justify-between transition-all mb-4 ${isDarkMode ? 'bg-white/5 border-white/5 hover:bg-white/10' : 'bg-gray-50 border-gray-100 hover:bg-gray-100'}`}>
                            <div className="flex items-center gap-4">
                                <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600'}`}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                </div>
                                <div>
                                    <div className="font-semibold text-base">Last Seen & Status</div>
                                    <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        Show when you are online
                                    </div>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowLastSeen(!showLastSeen)}
                                className={`relative w-14 h-8 rounded-full transition-colors duration-300 shadow-inner ${showLastSeen ? 'bg-green-500' : (isDarkMode ? 'bg-gray-700' : 'bg-gray-300')}`}
                            >
                                <span className={`absolute top-1 left-1 bg-white w-6 h-6 rounded-full shadow-md transform transition-transform duration-300 ${showLastSeen ? 'translate-x-6' : 'translate-x-0'}`}></span>
                            </button>
                        </div>

                        {/* Export Keys Removed for Security */}
                    </section>

                    <div className={`border-t ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}></div>

                    {/* 🎨 Theme Section */}
                    <section>
                        <h3 className={`text-xs font-bold uppercase tracking-wider mb-4 px-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Appearance</h3>

                        <div className={`p-4 rounded-2xl border flex items-center justify-between transition-all ${isDarkMode ? 'bg-white/5 border-white/5 hover:bg-white/10' : 'bg-gray-50 border-gray-100 hover:bg-gray-100'}`}>
                            <div className="flex items-center gap-4">
                                <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-indigo-500/20 text-indigo-400' : 'bg-orange-100 text-orange-500'}`}>
                                    {isDarkMode ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path></svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" /></svg>
                                    )}
                                </div>
                                <div>
                                    <div className="font-semibold text-base">{isDarkMode ? 'Dark Mode' : 'Light Mode'}</div>
                                    <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        Adjust the interface theme
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={toggleTheme}
                                className={`relative w-14 h-8 rounded-full transition-colors duration-300 shadow-inner ${isDarkMode ? 'bg-indigo-600' : 'bg-gray-300'}`}
                            >
                                <span className={`absolute top-1 left-1 bg-white w-6 h-6 rounded-full shadow-md transform transition-transform duration-300 ${isDarkMode ? 'translate-x-6' : 'translate-x-0'}`}></span>
                            </button>
                        </div>
                    </section>

                    <div className={`border-t ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}></div>

                    {/* 👤 Account Section */}
                    <section>
                        <h3 className={`text-xs font-bold uppercase tracking-wider mb-4 px-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Account Information</h3>
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div className="grid gap-5">
                                {/* Fields */}
                                <div>
                                    <label className={`block text-xs font-semibold mb-1.5 ml-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Full Name</label>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:ring-blue-500 outline-none transition-all ${isDarkMode ? 'bg-black/20 border-white/10 text-white placeholder-gray-600' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                                        placeholder="Your Name"
                                    />
                                </div>

                                <div>
                                    <label className={`block text-xs font-semibold mb-1.5 ml-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Username</label>
                                    <input
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:ring-blue-500 outline-none transition-all ${isDarkMode ? 'bg-black/20 border-white/10 text-white placeholder-gray-600' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                                        placeholder="@username"
                                    />
                                </div>

                                <div>
                                    <label className={`block text-xs font-semibold mb-1.5 ml-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Email</label>
                                    <input
                                        disabled
                                        value={email}
                                        className={`w-full px-4 py-3 rounded-xl border opacity-60 cursor-not-allowed ${isDarkMode ? 'bg-black/20 border-white/10 text-gray-400' : 'bg-gray-100 border-gray-200 text-gray-500'}`}
                                    />
                                </div>

                                <div>
                                    <label className={`block text-xs font-semibold mb-1.5 ml-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Phone</label>
                                    <input
                                        type="tel"
                                        value={mobile}
                                        onChange={(e) => setMobile(e.target.value)}
                                        className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:ring-blue-500 outline-none transition-all ${isDarkMode ? 'bg-black/20 border-white/10 text-white placeholder-gray-600' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                                        placeholder="+1 234 567 890"
                                    />
                                </div>
                            </div>

                            <div className="pt-2">
                                <button
                                    type="submit"
                                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-3.5 rounded-xl shadow-lg hover:shadow-blue-500/25 transition-all transform active:scale-[0.98]"
                                >
                                    Save Changes
                                </button>
                            </div>

                            {status && (
                                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-50 animate-fade-in-up">
                                    <div className={`px-6 py-3 rounded-full shadow-2xl flex items-center gap-2 font-semibold text-sm backdrop-blur-md border ${status.includes("✅")
                                        ? (isDarkMode ? 'bg-green-500/20 border-green-500/30 text-green-400' : 'bg-green-100 border-green-200 text-green-700')
                                        : (isDarkMode ? 'bg-red-500/20 border-red-500/30 text-red-400' : 'bg-red-100 border-red-200 text-red-700')}`}>
                                        <span>{status}</span>
                                    </div>
                                </div>
                            )}
                        </form>
                    </section>
                </div>
            </div>
        </div>
    );
}
